import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useApolloClient, useLazyQuery } from "@apollo/client/react";
import { GET_SETTINGS } from "@/hooks";
import { node } from "prop-types";
import {
  indexedDbDeleteAll,
  indexedDbDeleteById,
  indexedDbGetById,
  indexedDbUpdate,
} from "@/utils";
import { enqueueSnackbar } from "notistack";
import { useTextDomainContext } from "@/app/providers";

interface versionDataType {
  major: number;
  minor: number;
  release: number;
}

interface versionDataObjectType {
  settings: string | versionDataType;
}

export const VersionControl = (props: any) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const indexedDbStore = "persistent-data";
  const indexedDbKey = "version";
  const enabled = true;
  const timeout = 30 * 60 * 1000; // (30 minutes) First value is minutes

  const [newHotUpdate, setNewHotUpdate] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const client = useApolloClient();

  const [getVersion, { data: versionData, refetch: refetchVersion }] =
    useLazyQuery(GET_SETTINGS);

  const version = useMemo(() => {
    let versionDataToString = null;
    const vData = versionData as versionDataType;
    if (vData?.major || vData?.minor || vData?.release) {
      versionDataToString =
        vData.major + "." + vData.minor + "." + vData.release;
    }
    return newVersion || versionDataToString || null;
  }, [newVersion, versionData]);

  const latestVersion = useMemo(() => {
    let returnData = null;
    if (versionData) {
      let versionObject = JSON.parse('{"major": 0, "minor": 0, "release": 0}');
      let combinedVersion = "0.0.0";
      const vData = versionData as versionDataObjectType;
      if (vData.settings) {
        if (typeof vData.settings === "string") {
          try {
            versionObject = JSON.parse(vData.settings);
          } catch {
            // leave default if parse fails
          }
        } else {
          versionObject = vData.settings as versionDataType;
        }
      }

      if (
        versionObject.major > -1 &&
        versionObject.minor > -1 &&
        versionObject.release > -1
      ) {
        combinedVersion =
          versionObject.major +
          "." +
          versionObject.minor +
          "." +
          versionObject.release;
      }

      returnData = combinedVersion;
    }
    return returnData;
  }, [versionData]);

  const hotUpdate = useMemo(() => {
    let returnData = false;
    if (newHotUpdate) {
      returnData = true;
    }

    if (version && latestVersion && version !== latestVersion) {
      returnData = true;
    }
    return returnData;
  }, [latestVersion, newHotUpdate, version]);

  const checkLocalVersion = useCallback(() => {
    indexedDbGetById(indexedDbStore, indexedDbKey)
      .then((version) => {
        if (version) {
          setNewVersion(version);
        } else {
          // setHotUpdate(true);
        }
      })
      .catch(() => {
        setNewHotUpdate(true);
      });
  }, []);

  const checkVersion = useCallback(() => {
    getVersion({ variables: { key: "version" } }).then();
  }, [getVersion]);

  useEffect(() => {
    if (!enabled) return;

    checkLocalVersion();
    checkVersion();

    const interval = setInterval(() => {
      getVersion({ variables: { key: "version" } }).then(() => {
        refetchVersion().then(() => {});
      });
    }, timeout);

    return () => clearInterval(interval);
  }, [
    checkLocalVersion,
    checkVersion,
    enabled,
    getVersion,
    refetchVersion,
    timeout,
  ]);

  useEffect(() => {
    getVersion({
      variables: {
        key: "version",
      },
    }).then();
  }, [getVersion]);

  const manageUpdate = useCallback(() => {
    if (latestVersion) {
      indexedDbUpdate(indexedDbStore, latestVersion, indexedDbKey)
        .then(() => {
          indexedDbDeleteById("cache", "apollo-cache-persist").then(() => {
            indexedDbDeleteAll(["cache"]).then(() => {
              client.clearStore().then(() => {
                setTimeout(() => {
                  // window.location.reload(); // It is loading from cache.
                  window.location.replace(window.location.href);
                }, 10000);
              });
            });
          });
        })
        .catch(() => {});
    }
  }, [latestVersion, client]);

  useEffect(() => {
    if (hotUpdate) {
      enqueueSnackbar(
        gettext(
          "A newer version of this application is available. The update will begin automatically in 20 seconds.",
        ),
        {
          variant: "info",
          autoHideDuration: 20000,
        },
      );
      setTimeout(() => {
        manageUpdate();
      }, 10000);
    }
  }, [gettext, hotUpdate, manageUpdate]);

  return <>{props.children}</>;
};

VersionControl.propTypes = {
  children: node,
};
